import React from "react";
import { useTranslation } from "react-i18next";
import {
  useRolesRetrieval,
  useRetrieveCurrentPlanzone,
} from "@planzone/view-models";
import { Identifiable, Role as R } from "@planzone/dto";

import { useWorkspaceContext } from "@scenes/workspaceContext";
import { UserIcon } from "@common/assets/userIcon";
import { Loader } from "../loader";
import { Section } from "../section";
import { ScrollPane } from "../scrollPane";
import { filterSearch } from "../utils";
import { Props } from "./types";
import { Role } from "./role";
import styles from "./styles.scss";

function Roles({ search, filter, setFilter, isOpen, scope }: Props) {
  const { t } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const {
    isRequestPending,
    isRequestSuccess,
    isRequestFailure,
    roles,
    retrieveRoles,
  } = useRolesRetrieval();
  const ctx = useWorkspaceContext();
  const isProjectScope = scope ? scope === "project" : ctx.type === "project";
  const filteredRoles = filterSearch(
    roles
      .filter(({ name }) =>
        isProjectScope
          ? name.slice(0, "pr_".length) === "pr_"
          : name.slice(0, "pz_".length) === "pz_",
      )
      .map((role) => ({
        ...role,
        label: t(`roles.${role.name}`, role.name),
      })),
    search,
  );
  const selectRole = (role: Identifiable<R>) =>
    setFilter(filter ? [...filter, role] : [role]);
  const unSelectRole = ({ uuid }: Identifiable<R>) =>
    filter && setFilter(filter.filter((role) => role.uuid !== uuid));

  React.useEffect(() => {
    !isRequestPending &&
      !isRequestSuccess &&
      !isRequestFailure &&
      retrieveRoles(planzone);
  }, []);

  return (
    <Section
      header={
        <div className={styles.header}>
          <UserIcon className={styles.icon} />
          <p className={styles.title}>{t("common.filterMenu.roles.title")}</p>
        </div>
      }
      list={
        <ScrollPane>
          <div className={styles.list}>
            {!isRequestPending ? (
              [
                ...roles.filter(
                  ({ uuid }) => !!filteredRoles.find((r) => r.uuid === uuid),
                ),
              ].map((role) => (
                <Role
                  key={role.uuid}
                  role={role}
                  isSelected={
                    !!filter && !!filter.find(({ uuid }) => uuid === role.uuid)
                  }
                  select={() => selectRole(role)}
                  unSelect={() => unSelectRole(role)}
                />
              ))
            ) : (
              <Loader />
            )}
          </div>
        </ScrollPane>
      }
      isOpen={isOpen}
    />
  );
}

export { Roles };
